import { useEffect, useState } from 'react';

/**
 * Reads the authenticated user from props or falls back to localStorage (`auth_user`).
 * Returns null when no user is available.
 *
 * Re-reads from localStorage when:
 *  - The window regains focus (catches same-tab updates after navigating away and back).
 *  - A `storage` event fires for the `auth_user` key (catches cross-tab updates).
 *  - A custom `skeptics:auth-user-updated` event is dispatched on window (allows
 *    in-app code — e.g. integration settings — to notify without a page reload):
 *
 *      window.dispatchEvent(new Event('skeptics:auth-user-updated'));
 *
 * This keeps has_own_ai_credentials (and other computed fields written by the server
 * into auth_user) in sync after mid-session changes such as adding an AI integration.
 */
function getStoredAuthUser() {
  if (typeof window === 'undefined') return null;
  try {
    const stored = window.localStorage.getItem('auth_user');
    if (!stored) return null;
    const parsed = JSON.parse(stored);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function useAuthUser(initialUser = null) {
  const [user, setUser] = useState(initialUser || getStoredAuthUser());

  // Hydrate on first render when no initial user was passed.
  useEffect(() => {
    if (user || typeof window === 'undefined') return;
    const stored = getStoredAuthUser();
    if (stored) setUser(stored);
  }, [user]);

  // Re-read auth_user from localStorage on focus, storage events, and the
  // custom in-app event so computed flags (e.g. has_own_ai_credentials) stay
  // current after mid-session changes without requiring a full sign-out/sign-in.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const rehydrate = () => {
      const fresh = getStoredAuthUser();
      // Only update state when the stored value has actually changed to avoid
      // unnecessary re-renders on unrelated focus/storage events.
      if (fresh && JSON.stringify(fresh) !== JSON.stringify(user)) {
        setUser(fresh);
      }
    };

    const handleStorage = (e) => {
      if (e.key === 'auth_user') rehydrate();
    };

    window.addEventListener('focus', rehydrate);
    window.addEventListener('storage', handleStorage);
    window.addEventListener('skeptics:auth-user-updated', rehydrate);

    return () => {
      window.removeEventListener('focus', rehydrate);
      window.removeEventListener('storage', handleStorage);
      window.removeEventListener('skeptics:auth-user-updated', rehydrate);
    };
  }, [user]);

  return user;
}

export default useAuthUser;
